import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Spin } from 'antd';

import { fetchNewsArticle, saveNews } from './newsApi';

type Props = {
  visible: boolean;
  hide: () => void;
  newsId: number;
};

/** The story's body, read when opened: the news table itself never carries bodies. */
const NewsBodyModal: React.FC<Props> = ({ visible, hide, newsId }) => {
  const [body, setBody] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetchNewsArticle(newsId, controller.signal)
      .then(article => {
        if (!controller.signal.aborted) setBody(article.body);
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setError(reason.message || 'Could not load the body.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [newsId]);

  return (
    <Modal
      open={visible}
      title={`News #${newsId} body`}
      onCancel={hide}
      okText="Save body"
      okButtonProps={{
        disabled: loading || body === undefined,
        loading: saving,
      }}
      onOk={async () => {
        setSaving(true);
        setError('');
        try {
          await saveNews({ id: newsId, body: body || '' });
          hide();
        } catch (reason) {
          setError(
            reason instanceof Error ? reason.message : 'Could not save.',
          );
        } finally {
          setSaving(false);
        }
      }}
      width={800}
    >
      {error && <Alert type="error" showIcon title={error} />}
      <Spin spinning={loading}>
        <Input.TextArea
          aria-label={`Body text of news #${newsId}`}
          rows={16}
          value={body}
          onChange={event => setBody(event.target.value)}
        />
      </Spin>
    </Modal>
  );
};

export default NewsBodyModal;
